// src/store/compatibility.js
// A compatibility layer to help migrate from the legacy store to Redux

import { useSelector, useDispatch } from 'react-redux';
import { setSnackMessage } from '../reduxStore/modules/common';
import { login } from '../reduxStore/modules/user/actions';

/**
 * Deprecated useStore hook compatibility layer
 * This returns a state object that mimics the old store structure
 * and a dispatch function that works with Redux
 * 
 * WARNING: This is a temporary compatibility layer and should be replaced
 * with proper Redux hooks in individual components
 */
export const useStore = () => {
  console.warn('[DEPRECATED] Legacy useStore is deprecated. Please migrate to useSelector and useDispatch from react-redux.');
  
  const dispatch = useDispatch();
  
  // Build a combined state object that mimics the old store structure
  const user = useSelector(state => state.user?.user);
  const userState = useSelector(state => state.user);
  const filterOptions = useSelector(state => state.feed?.filterOptions);
  const filterSelections = useSelector(state => state.feed?.filterSelections);
  const totalRequests = useSelector(state => state.feed?.totalRequests);
  const i18n = useSelector(state => state.common?.i18n);
  const defaultUnit = useSelector(state => state.user?.defaultUnit);
  const accountUsage = useSelector(state => state.user?.accountUsage);
  const billing = useSelector(state => state.billing);
  const accountSettings = useSelector(state => state.user?.accountSettings);
  const email = user?.Email || '';
  const loggedIn = userState?.isLoggedIn || false; // Add loggedIn state for compatibility
  
  // This simulates the legacy store structure
  const storeState = {
    user,
    userState,
    filterOptions,
    filterSelections,
    totalRequests,
    i18n,
    defaultUnit,
    accountUsage,
    billing,
    accountSettings,
    email,
    loggedIn
  };
  
  // Create a wrapper around dispatch to handle legacy action types
  const legacyDispatch = (action) => {
    // Map legacy action types to new Redux actions
    switch (action.type) {
      case 'ACTION_LOGIN':
        // For login action, use the new Redux login action
        if (action.email && action.token) {
          console.warn('[DEPRECATED] Legacy ACTION_LOGIN detected. Using Redux login action instead.');
          // We can't directly call login with password as we don't have it anymore
          // Instead, we'll just set the basic user info in Redux
          dispatch({ 
            type: 'user/LOGIN', 
            payload: { 
              email: action.email, 
              token: action.token,
              user: null  // Will be populated later by onAuthStateChanged
            } 
          });
        }
        break;
        
      case 'ACTION_SET_SNACK_MSG':
        dispatch(setSnackMessage({
          message: action.snackMessage.message,
          variant: action.snackMessage.level,
          duration: action.snackMessage.duration
        }));
        break;
      case 'ACTION_SET_RECHARGE_POPUP':
        if (typeof action.open !== 'undefined') {
          dispatch({ type: 'drawer/SET_RECHARGE_POPUP', payload: action.open });
        }
        break;
      case 'ACTION_SET_UPGRADE_POPUP':
        if (typeof action.open !== 'undefined') {
          dispatch({ type: 'drawer/SET_UPGRADE_POPUP', payload: action.open });
        }
        break;
      case 'ACTION_SET_FILTER_FEED_PAGE_NB':
        if (typeof action.pageNb !== 'undefined') {
          dispatch({ type: 'feed/SET_FILTER_PAGE_NUMBER', payload: action.pageNb });
        }
        break;
      case 'ACTION_SET_TENANTS_ENABLE_UPLOAD':
        if (typeof action.tenantEnableUpload !== 'undefined') {
          dispatch({ type: 'tenants/SET_TENANT_ENABLE_UPLOAD', payload: action.tenantEnableUpload });
        }
        break;
      // Add more mappings as needed
        
      default:
        // For unhandled legacy actions, log and dispatch as-is
        console.warn(`[DEPRECATED] Unmapped legacy action type: ${action.type}`, action);
        dispatch(action);
    }
  };
  
  return [storeState, legacyDispatch];
};

// Export other compatibility utilities as needed 